import { useEffect, useRef } from 'react'
import { init, dispose, LoadDataType } from 'klinecharts'
import type { Chart, KLineData } from 'klinecharts'
import { DatafeedSocket, type RawCandleRow } from '@/bot/adapters/datafeedSocket'
import { registerWorkbenchOverlays } from './customOverlays'
import { registerVolumeOverlay } from './volumeOverlay'
import { buildStyles, defaultBackground } from './chartStyles'
import { chartTypeById } from './catalog'
import { toHeikinAshi } from './heikinAshi'
import { useIsDark } from './useIsDark'
import type { ChartSettings, ChartTypeId } from './types'

export interface WorkbenchChartStatus {
  loading: boolean
  error: string | null
  empty: boolean
  /** The symbol this status belongs to — lets the orchestrator's per-symbol
   * restore ignore a stale load that lands after another symbol switch. */
  symbol?: string
}

export interface WorkbenchChartProps {
  symbol: string
  timeframe: string
  /** Anchor (unix seconds) for the most recent candle to show; null = now. */
  anchorTs: number | null
  /** Decimal places for prices (from the symbol's point size); null = chart default. */
  pricePrecision: number | null
  chartType: ChartTypeId
  settings: ChartSettings
  /** Hands the raw klinecharts instance up on init; null again on dispose. */
  onReady: (chart: Chart | null) => void
  onStatusChange?: (status: WorkbenchChartStatus) => void
}

const INITIAL_BARS = 800
const PAGE_BARS = 500

/**
 * klinecharts prints a dev-only "Welcome to klinecharts" banner synchronously
 * inside init() with no option to disable it — mute console.log for just that
 * one line while init() runs.
 */
function initQuietly(el: HTMLElement): Chart | null {
  const nativeLog = console.log
  console.log = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].includes('Welcome to klinecharts')) return
    nativeLog(...args)
  }
  try {
    return init(el)
  } finally {
    console.log = nativeLog
  }
}

function toKline(rows: RawCandleRow[]): KLineData[] {
  return rows.map(([time, open, high, low, close, volume]) => ({
    timestamp: time * 1000,
    open,
    high,
    low,
    close,
    volume,
  }))
}

/**
 * The klinecharts engine wrapper. Owns init/dispose, overlay/indicator
 * registrations, candle loading over the datafeed WebSocket (initial window +
 * pan-left pagination), price precision, and the theme/settings/chart-type
 * styling. Everything else (tools, indicators, drawings) is driven by the
 * orchestrator through the raw chart instance handed up via `onReady` — the
 * chart is never torn down on symbol/type switches, so overlays and indicators
 * survive them.
 */
export function WorkbenchChart({
  symbol,
  timeframe,
  anchorTs,
  pricePrecision,
  chartType,
  settings,
  onReady,
  onStatusChange,
}: WorkbenchChartProps) {
  const isDark = useIsDark()

  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<Chart | null>(null)
  const socketRef = useRef<DatafeedSocket | null>(null)

  // Latest values for use inside long-lived chart callbacks.
  const symbolRef = useRef(symbol)
  const timeframeRef = useRef(timeframe)
  const chartTypeRef = useRef(chartType)
  const onStatusRef = useRef(onStatusChange)
  const onReadyRef = useRef(onReady)
  useEffect(() => {
    symbolRef.current = symbol
    timeframeRef.current = timeframe
    chartTypeRef.current = chartType
    onStatusRef.current = onStatusChange
    onReadyRef.current = onReady
  })

  // Raw (untransformed) bars as loaded — the source of truth for chart-type
  // switches, so toggling Heikin Ashi on/off is lossless.
  const rawRef = useRef<KLineData[]>([])
  const moreRef = useRef(false)
  // Guards async loads against symbol/timeframe changes.
  const loadSeqRef = useRef(0)

  const displayBars = (bars: KLineData[]): KLineData[] =>
    chartTypeById(chartTypeRef.current).ha ? toHeikinAshi(bars) : bars

  // ---- Init / dispose -------------------------------------------------------
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    registerWorkbenchOverlays() // no-op after first call
    registerVolumeOverlay()
    const chart = initQuietly(el)
    chartRef.current = chart
    try {
      socketRef.current = new DatafeedSocket()
    } catch (err) {
      onStatusRef.current?.({
        loading: false,
        error: err instanceof Error ? err.message : 'Datafeed not configured',
        empty: true,
      })
    }

    // Pan-left pagination: load older candles when the user scrolls back.
    // Note: in Heikin Ashi mode each older chunk is transformed on its own
    // (seeded from its first raw candle) — see heikinAshi.ts for the seam caveat.
    chart?.setLoadDataCallback(({ type, data, callback }) => {
      if (type !== LoadDataType.Forward || !data?.timestamp) {
        callback([], false)
        return
      }
      const toSec = Math.floor(data.timestamp / 1000) - 1
      socketRef.current
        ?.requestHistory({
          symbol: symbolRef.current,
          timeframe: timeframeRef.current,
          to: toSec,
          count: PAGE_BARS,
        })
        .then((rows) => {
          const bars = toKline(rows)
          rawRef.current = [...bars, ...rawRef.current]
          moreRef.current = rows.length > 0
          callback(displayBars(bars), rows.length > 0)
        })
        .catch(() => callback([], false))
    })

    // klinecharts sizes its canvases once at init — follow container resizes
    // (window, chat-panel drag handle, sidebar collapse) explicitly.
    const ro = new ResizeObserver(() => chartRef.current?.resize())
    ro.observe(el)

    onReadyRef.current(chart)

    return () => {
      ro.disconnect()
      onReadyRef.current(null)
      socketRef.current?.dispose()
      socketRef.current = null
      dispose(el)
      chartRef.current = null
      rawRef.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- Theme / settings / chart-type styling --------------------------------
  useEffect(() => {
    chartRef.current?.setStyles(buildStyles(isDark, settings, chartTypeById(chartType).engine))
  }, [isDark, settings, chartType])

  // ---- Price precision --------------------------------------------------------
  // Drives both the y-axis auto-scale (klinecharts pads the range by 10^-precision
  // when the visible spread is tiny — a too-coarse default squashes quiet windows
  // into a thin strip) and the y-axis tick / price-mark formatting.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart || pricePrecision == null) return
    chart.setPriceVolumePrecision(pricePrecision, 0)
  }, [pricePrecision])

  // ---- Initial load on symbol / timeframe / anchor change --------------------
  useEffect(() => {
    const chart = chartRef.current
    const socket = socketRef.current
    if (!chart || !socket || !symbol) return

    const seq = ++loadSeqRef.current
    onStatusRef.current?.({ loading: true, error: null, empty: false, symbol })

    const toSec = anchorTs ?? Math.floor(Date.now() / 1000)
    socket
      .requestHistory({ symbol, timeframe, to: toSec, count: INITIAL_BARS })
      .then((rows) => {
        if (seq !== loadSeqRef.current) return // superseded
        rawRef.current = toKline(rows)
        moreRef.current = rows.length > 0
        // `more: true` lets a left-scroll trigger the load callback.
        chart.applyNewData(displayBars(rawRef.current), moreRef.current)
        onStatusRef.current?.({ loading: false, error: null, empty: rows.length === 0, symbol })
      })
      .catch((err: Error) => {
        if (seq !== loadSeqRef.current) return
        rawRef.current = []
        chart.applyNewData([], false)
        onStatusRef.current?.({ loading: false, error: err.message, empty: true, symbol })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe, anchorTs])

  // ---- Re-shape data on chart-type change ------------------------------------
  // Only Heikin Ashi transforms the raw bars; bar / candle / line share identical
  // OHLC data and differ purely in the engine candle type, which the styling
  // effect above already applies via setStyles. So re-apply data ONLY when the HA
  // flag flips — an unconditional applyNewData resets the scroll offset and snaps
  // the view back to the latest bar, discarding the range the user is looking at.
  // Across an HA flip, preserve the viewport by re-anchoring the last visible bar
  // (same pattern as the mount restore in Workbench). Timestamps survive the HA
  // transform; the raw offset does NOT round-trip (getOffsetRightDistance clamps
  // at 0 once scrolled back into history).
  const prevHaRef = useRef(chartTypeById(chartType).ha)
  useEffect(() => {
    const chart = chartRef.current
    const ha = chartTypeById(chartType).ha
    const wasHa = prevHaRef.current
    prevHaRef.current = ha
    if (!chart || rawRef.current.length === 0 || ha === wasHa) return
    // vr.to < bar count ⇔ scrolled back into history. At the live edge the reset
    // applyNewData performs already reproduces the prior view (default margin).
    const vr = chart.getVisibleRange()
    const scrolledBack = vr.to < chart.getDataList().length
    chart.applyNewData(displayBars(rawRef.current), moreRef.current)
    if (scrolledBack) {
      // Bar indices survive the HA transform (1:1). The fractional -1.5 anchors
      // the last visible bar half a bar inside the right edge — the fixed point
      // of the engine's half-open visible-range rounding (`to = round(… + 0.5)`),
      // so repeated flips don't ratchet the view sideways a bar at a time.
      // scrolling is a no-op while a drawing tool holds the scroll lock.
      const scrollLocked = !chart.isScrollEnabled()
      if (scrollLocked) chart.setScrollEnabled(true)
      chart.scrollToDataIndex(Math.max(0, vr.to - 1.5))
      if (scrollLocked) chart.setScrollEnabled(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType])

  return (
    <div
      ref={containerRef}
      className="absolute inset-0"
      style={{ backgroundColor: settings.background || defaultBackground(isDark) }}
    />
  )
}
